/**
 * Human readable groupings of attachment file types. These are the values
 * surfaced in the "Attachment type" filter and persisted to the URL, so they
 * are deliberately readable and stable — the mime types they expand to are an
 * implementation detail of the backend query.
 */
export const attachmentFileTypes = [
	'images',
	'documents',
	'pdfs',
	'presentations',
	'spreadsheets',
	'videos',
	'audio',
] as const;

export type AttachmentFileType = (typeof attachmentFileTypes)[number];

/**
 * Mime types each file type grouping expands to when querying the backend.
 */
export const attachmentFileTypeMimeTypes: Record<AttachmentFileType, readonly string[]> = {
	images: ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff', 'image/webp'],
	documents: [
		'application/msword',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		'text/plain',
		'text/rtf',
		'text/html',
		'application/xml',
		'application/json',
	],
	pdfs: ['application/pdf'],
	presentations: [
		'application/vnd.ms-powerpoint',
		'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	],
	spreadsheets: [
		'application/vnd.ms-excel',
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		'text/csv',
	],
	videos: [
		'video/mp4',
		'video/quicktime',
		'video/x-msvideo',
		'video/x-matroska',
		'video/webm',
		'video/x-flv',
		'video/x-m4v',
		'video/x-ms-asf',
		'video/3gpp',
	],
	audio: [
		'audio/mp3',
		'audio/mp4',
		'audio/mpeg',
		'audio/x-wav',
		'audio/x-hx-aac-adts',
		'audio/x-m4a',
		'audio/ogg',
	],
};

/**
 * Type guard for values coming from untrusted sources such as the URL.
 */
export const isAttachmentFileType = (value: unknown): value is AttachmentFileType =>
	attachmentFileTypes.some((fileType) => fileType === value);

/**
 * Expands file type groupings into the flat list of mime types the backend
 * attachment search expects.
 */
export const toMimeTypes = (fileTypes: readonly AttachmentFileType[]): string[] =>
	fileTypes.flatMap((fileType) => attachmentFileTypeMimeTypes[fileType]);
